(function () {
    function APIService(
        $http,
        $timeout,
        $q,
        DashboardConfig
    ) {

        const self = this;

        /**
         * Initialises common HTTP headers
         */
        self.initialiseRequestHeaders = function () {
            $http.defaults.headers.common["Content-Type"] = "application/json";
        };

        /**
         * Generates the full endpoint URL
         * @param endpoint
         * @returns {string}
         */
        self.endpoint = function (endpoint) {
            const
                action = endpoint.startsWith("/") ? endpoint.substr(1) : endpoint,
                apiBase = DashboardConfig.APIServer.endsWith("/")
                    ? DashboardConfig.APIServer
                    : `${DashboardConfig.APIServer}/`;
            return `${apiBase}${action}`;
        };

        function isMethodGetOrDelete(method) {
            return !method || /get|delete/i.test(method);
        }

        /**
         * given an object with undefined properties, URLSearchParams will store those as string values of "undefined",
         * so we strip them out with a quick serialize/deserialize
         */
        function createUrlSearchParamsFor(payload) {
            if (!payload || typeof payload !== "object") {
                return [];
            }
            const safePayload = payload
                ? JSON.parse(JSON.stringify(payload))
                : payload
            return new URLSearchParams(safePayload);
        }

        function getTransformedRequest(endpoint, method, payload) {
            const slug = endpoint.startsWith("/") ? endpoint.substr(1) : endpoint;
            const url = new URL(DashboardConfig.APIServer + "/" + slug)

            if (!isMethodGetOrDelete(method)) {
                return {
                    method,
                    url: url.toString(),
                    data: payload
                }
            }

            const params = createUrlSearchParamsFor(payload);
            for (const [ key, value ] of params) {
                url.searchParams.set(key, value)
            }
            return {
                method: method || 'GET',
                url: url.toString()
            }
        }

        self.queryServer = function (
            endpoint,
            verb,
            data,
            options
        ) {
            const defaultOptions = {
                headers: {},
                showLoader: false
            };

            options = _.defaults(options || {}, defaultOptions);

            const defer = $q.defer();
            $http({
                headers: options.headers,
                timeout: DashboardConfig.HttpTimeout,
                withCredentials: true,
                startTime: new Date().getTime(),
                showLoader: options.showLoader,
                ...getTransformedRequest(endpoint, verb, data)
            }).then(
                function success(result) {
                    defer.resolve(result);
                },
                function failure(result) {
                    defer.reject(result);
                }
            );
            return defer.promise;
        };

        self.queryUrl = function (
            endpoint,
            verb,
            data,
            options
        ) {
            const defaultOptions = {
                headers: {},
                showLoader: false
            };

            options = _.defaults(options || {}, defaultOptions);

            const defer = $q.defer();
            $http({
                url: endpoint,
                headers: options.headers,
                method: verb || "GET",
                data: data,
                timeout: DashboardConfig.HttpTimeout,
                startTime: new Date().getTime(),
                showLoader: options.showLoader
            }).then(
                function success(result) {
                    defer.resolve(result);
                },
                function failure(result) {
                    defer.reject(result);
                });

            return defer.promise;
        };

        self.Settings = {
            fetchChatAssistant: () => {
                const endpoint = "/mapi/settings/chat-assistant";
                return self.queryServer(endpoint, "GET")
                    .then(res => {
                        return res.data;
                    })
                    .catch(e => {
                        console.warn(
                            "Unable to fetch chat assistant setting, reverting to none",
                            e
                        );
                        return "None";
                    });
            }
        };

        self.Account = {

            getUserProfile: function () {
                const endpoint = "/mapi/user";
                return self.queryServer(endpoint, "GET", null, {
                    showLoader: true
                });
            }
        };

        self.LandingPages = {
            fetchLandingPages: function (filter, page, count) {
                const params = createUrlSearchParamsFor({
                    ...filter,
                    page,
                    count
                })

                return self.queryServer(`/mapi/landingpages?${params}`)
            },
            fetchLandingPageTemplates: function (brandId) {
                let endpoint = "/mapi/landingpages/templates";
                endpoint = endpoint.appendQuerystringParameter("brandId", brandId);
                return self.queryServer(endpoint, "GET");
            },
            fetchLandingPageById: function (id) {
                return self.queryServer(`/mapi/landingpages/${id}`)
            },
            uploadFeaturedImage: function (id, files) {
                const endpoint = `/mapi/landingpages/${id}/featured-image`
                const formData = new FormData();
                const file = files[0];
                formData.append('image', file, file.filename);

                return self.queryServer(endpoint, "POST", formData, {
                    headers: {
                        'Content-Type': undefined
                    }
                });
            },
            update: function (id, payload) {
                return self.queryServer(
                    `/mapi/landingpages/${id}`,
                    "PUT",
                    payload)
            },
            updateContent(id, payload) {
                return self.queryServer(
                    `/mapi/landingpages/${id}/content`,
                    "PUT",
                    payload)
            },
            publish(id, payload) {
                return self.queryServer(
                    `/mapi/landingpages/${id}/publish`,
                    "POST",
                    payload);
            },
            create: function (payload) {
                return self.queryServer(`/mapi/landingpages`, "POST", payload)
            },
            delete: function (id) {
                return self.queryServer(`/mapi/landingpages/${id}`, "DELETE")
            },
            fetchDisplayOrder(brandId) {
                return self.queryServer(`/mapi/landingpages/display-order?brandId=${brandId}`)
            },
            updateDisplayOrder(items) {
                return self.queryServer(`/mapi/landingpages/display-order`, "PUT", items)
            },
            removeFeaturedImage(pageId) {
                return self.queryServer(`/mapi/landingpages/${pageId}/featured-image`, "DELETE")
            },
            updatePlacement(id, payload) {
                return self.queryServer(
                    `/mapi/landingpages/${id}/placement`,
                    "PUT",
                    payload
                )
            }
        };

        self.Permissions = {

            getAllPermissionsRoles: function () {
                const endpoint = "mapi/roles/rolepermissions";
                return self.queryServer(endpoint, "GET", null, {
                    showLoader: true
                });
            },

            /**
             * Gets all the roles for the different contexts
             */
            getRolesForContextLookup: function () {
                const endpoint = "/mapi/roles/contextlookup";
                return self.queryServer(endpoint, "GET", null, {
                    showLoader: true
                });
            }
        };

        self.Roles = {
            /**
             * Gets the roles with their context requirements
             */
            getRoleContextRequirementMapping: function () {
                const endpoint = "mapi/roles/contextrequirements";

                return self.queryServer(endpoint, "GET");
            }
        };

        self.Brands = {
            getBrandsByBrandIds: function (brandIds) {
                const endpoint = "/mapi/brands";
                return self.queryServer(endpoint, "POST", brandIds);
            },
            getBrandByBrandId: function (brandId) {
                let endpoint = "/mapi/brand";
                endpoint = endpoint.appendQuerystringParameter("id", brandId);
                return self.queryServer(endpoint, "GET");
            }
        };

        self.CallCenters = {

            getCallCentersByCallCenterIds: function (callCenterIds) {
                const endpoint = "/mapi/callcenters";
                return self.queryServer(endpoint, "POST", callCenterIds);
            },
            getCallCenterGroupsByCallCenterId: function (callCenterId) {
                if (!callCenterId) {
                    throw Error(`'${callCenterId}' is not a valid call center id`);
                }

                const endpoint = `/mapi/callcenters/${callCenterId}/groups`;
                return self.queryServer(endpoint, "GET", null);
            }
        };

        self.Stores = {

            getMenuUrl: function (storeId) {
                let endpoint = "/mapi/store/menuurl";
                endpoint = endpoint.appendQuerystringParameter("storeId", storeId);
                return self.queryServer(endpoint, "GET");
            },

            getStoresByStoreIds: function (storeIds) {
                const endpoint = "/mapi/stores";
                return self.queryServer(endpoint, "POST", storeIds);
            },

            getStoreById(storeId) {
                const defer = $q.defer();
                let endpoint = "/mapi/store";
                endpoint = endpoint.appendQuerystringParameter("storeId", storeId);
                self.queryServer(endpoint, "GET")
                    .then(function (result) {
                        const store = result.data;
                        defer.resolve({ data: store });
                    }, function (error) {
                        defer.reject(error);
                    });
                return defer.promise;
            },

            getStoresForBrand: function (brandId,
                                         includeTestStores,
                                         includeUnavailableStores) {
                const endpoint = "/mapi/brands/stores".appendQuerystringParameters({
                    brandId,
                    includeTestStores,
                    includeUnavailableStores
                });
                return self.queryServer(endpoint, "GET");
            },

            getStoresForCallCenter: function (callCenterId) {
                let endpoint = "/mapi/callcenters/stores";
                endpoint = endpoint.appendQuerystringParameter("callCenterId", callCenterId);
                return self.queryServer(endpoint, "GET");
            },

            getStoreSubscriptions: function (storeId) {
                let endpoint = "/mapi/stores/subscriptions";
                endpoint = endpoint.appendQuerystringParameter("storeId", storeId);
                return self.queryServer(endpoint, "GET");
            },

            getSubscriptionByName: function (subscriptionName) {
                let endpoint = "/mapi/stores/subscription";
                endpoint = endpoint.appendQuerystringParameter("subscriptionName", subscriptionName);
                endpoint = endpoint.appendQuerystringParameter("includeTemplate", true);
                return self.queryServer(endpoint, "GET");
            },

            getSubscriptionById: function (subscriptionId) {
                let endpoint = "/mapi/stores/subscription";
                endpoint = endpoint.appendQuerystringParameter("subscriptionId", subscriptionId);
                endpoint = endpoint.appendQuerystringParameter("includeTemplate", true);
                return self.queryServer(endpoint, "GET");
            },

            subscribeStore: function (subscriptionId, storeId) {
                const endpoint = "/mapi/stores/subscription";
                const request = {
                    subscriptionId: subscriptionId,
                    storeId: storeId
                };
                return self.queryServer(endpoint, "POST", request);
            },
            toggleAllowOnlineOrders: function (storeId, allowOnlineOrders) {
                let endpoint = "/mapi/stores/toggleorders";
                endpoint = endpoint.appendQuerystringParameter("storeId", storeId);
                endpoint = endpoint.appendQuerystringParameter("allowOnlineOrders", allowOnlineOrders);
                return self.queryServer(endpoint, "POST");
            },
            toggleOrderCompleteNotifications: function (storeId, enabled) {
                let endpoint = "/mapi/stores/toggleordercompletenotifications";
                endpoint = endpoint.appendQuerystringParameter("storeId", storeId);
                endpoint = endpoint.appendQuerystringParameter("sendNotifications", enabled);
                return self.queryServer(endpoint, "POST");
            },
            updateKerbsideCollectMobileNumber: function (storeId, mobileNumber) {
                let endpoint = "/mapi/stores/updatekerbsidemobile";
                endpoint = endpoint.appendQuerystringParameter("storeId", storeId);
                endpoint = endpoint.appendQuerystringParameter("mobileNumber", mobileNumber);
                return self.queryServer(endpoint, "POST");
            }
        };

        self.Groups = {
            getGroupsByGroupIds: function (groupIds) {
                const endpoint = "/mapi/groups";
                return self.queryServer(endpoint, "POST", groupIds);
            }
        };

        self.OrderUp = {
            getOrders: function (storeId) {
                const endpoint = "api/orderup/orders".appendQuerystringParameter(
                    "storeId",
                    storeId
                );
                return self.queryServer(endpoint, "GET");
            },
            acceptOrder: function (orderGuid, delayTime) {
                const endpoint = "api/orderup/order/accept".appendQuerystringParameter(
                    "orderGuid",
                    orderGuid
                ).appendQuerystringParameter(
                    "delayTime",
                    delayTime
                );
                return self.queryServer(endpoint, "PUT");
            },
            declineOrder: function (
                orderGuid,
                rejectReason
            ) {
                const endpoint = "api/orderup/order/decline".appendQuerystringParameter(
                    "orderGuid",
                    orderGuid
                ).appendQuerystringParameter(
                    "rejectReason",
                    rejectReason
                );
                return self.queryServer(endpoint, "PUT");
            },
            captureOrder: function (orderGuid) {
                const endpoint = "api/orderup/order/capture".appendQuerystringParameter(
                    "orderGuid",
                    orderGuid
                );
                return self.queryServer(endpoint, "PUT");
            },
            removeOrder: function (orderGuid) {
                let endpoint = "api/orderup/order/remove";
                endpoint = endpoint.appendQuerystringParameter("orderGuid", orderGuid);
                return self.queryServer(endpoint, "PUT");
            },
            markOrderReady: function (orderGuid) {
                let endpoint = "api/orderup/order/mark-ready";
                endpoint = endpoint.appendQuerystringParameter("orderGuid", orderGuid);
                return self.queryServer(endpoint, "PUT");
            },
            heartbeat: function (storeId) {
                const endpoint = `api/orderup/heartbeat/${storeId}`;
                return self.queryServer(endpoint, "GET");
            },
            enableOrderUpChannel: function (storeId) {
                return self.queryServer(`api/orderup/enable/${storeId}`, "GET");
            },
            disableOrderUpChannel: function (storeId) {
                return self.queryServer(`api/orderup/disable/${storeId}`, "GET");
            },
            fetchRTAPISessionDetails: function (storeId) {
                return self.queryServer(
                    `api/orderup/session/${storeId}`,
                    "GET"
                );
            }
        };

        self.Provinces = {
            getProvincesByProvinceIds: function (provinceIds) {
                const endpoint = "/mapi/provinces";
                return self.queryServer(endpoint, "POST", provinceIds);
            },
            getProvinceById: function (provinceId) {
                const defer = $q.defer();
                const endpoint = "/mapi/provinces";
                self.queryServer(endpoint, "POST", [ provinceId ])
                    .then(function (result) {
                        const province = result.data[0];
                        defer.resolve({ data: province });
                    }, function (error) {
                        defer.reject(error);
                    });
                return defer.promise;
            },
            listAllProvinces: function () {
                const endpoint = "/mapi/provinces";
                return self.queryServer(endpoint, "GET");
            }
        };

        self.Users = {
            getUserById: function (
                userId,
                brandId,
                storeId,
                callCenterId
            ) {
                const endpoint = `/mapi/users/${userId}`;
                return self.queryServer(
                    endpoint,
                    "GET", {
                        storeId,
                        brandId,
                        callCenterId
                    });
            },

            getUsersForContext: function (take, skip, context, contextCollection, filterText) {
                let endpoint = "/mapi/{0}/users";

                const request = {
                    Skip: skip,
                    Take: take
                };

                if (filterText) {
                    request.FilterText = filterText;
                }

                switch (context.ContextType) {
                    case Dashboard.ContextTypes.BrandContext:
                        endpoint = endpoint.format("brands");
                        request.BrandIds = contextCollection;
                        break;
                    case Dashboard.ContextTypes.CallCenterContext:
                        endpoint = endpoint.format("callcenters");
                        request.CallCenterIds = contextCollection;
                        break;
                    case Dashboard.ContextTypes.StoreContext:
                        endpoint = endpoint.format("stores");
                        request.StoreIds = contextCollection;
                        break;
                }

                endpoint = endpoint.appendQuerystringParameters(request);
                return self.queryServer(endpoint, "GET");
            },

            getManageableRoles: function (context) {
                let endpoint = "/mapi/user/manageableroles";

                switch (context.ContextType) {
                    case Dashboard.ContextTypes.BrandContext:
                        endpoint = endpoint.appendQuerystringParameter("rolecontext", "brand");
                        break;
                    case Dashboard.ContextTypes.CallCenterContext:
                        endpoint = endpoint.appendQuerystringParameter("rolecontext", "callcenter");
                        break;
                    case Dashboard.ContextTypes.StoreContext:
                        endpoint = endpoint.appendQuerystringParameter("rolecontext", "store");
                        break;
                }

                return self.queryServer(endpoint, "GET");
            },
            saveUser: function (user) {
                const endpoint = "/mapi/users";
                return self.queryServer(endpoint, "PUT", user);
            },
            getManageableUserByEmail: function (email, brandIds, callCenterIds, storeIds) {
                const endpoint = "/mapi/user/manageableuser";

                const request = {
                    UserEmail: email,
                    BrandIds: brandIds,
                    CallCenterIds: callCenterIds,
                    StoreIds: storeIds
                };

                return self.queryServer(endpoint, "POST", request);
            },
            getUserByEmail: (email) => {
                const endpoint = `/mapi/user/${encodeURIComponent(email)}`;
                return self.queryServer(endpoint, "GET");
            },
            addUserRoles: function (userId, roles) {
                let endpoint = `/mapi/users/${userId}/roles`;
                return self.queryServer(
                    endpoint,
                    "POST",
                    roles
                );
            },
            deleteUserRoles: function (userId, roles) {
                let endpoint = `/mapi/users/${userId}/roles/delete`;
                return self.queryServer(
                    endpoint,
                    "POST",
                    roles
                );
            },
            requestPasswordResetForUser: function (userId) {
                let endpoint = `/mapi/users/${userId}/reset-password`;
                return self.queryServer(endpoint, "POST", null);
            },
            createUser: (request) => {
                const endpoint = "/mapi/users";
                return self.queryServer(endpoint, "POST", request);
            }
        };


        self.Vouchers = {
            GetVoucherRequestTemplate: function () {
                const endpoint = "/mapi/voucher";
                return self.queryServer(endpoint, "GET");
            },
        };

        self.Orders = {

            refundOrder: function (request) {
                const endpoint = "mapi/order/refund";
                return self.queryServer(endpoint, "POST", request);
            },
            fetchTerminals: function (orderId) {
                const endpoint = "mapi/order/terminal/{0}".format(orderId);
                return self.queryServer(endpoint, "GET");
            },
            getOrders: function (filter, userContext) {
                const endpoint = "mapi/orders";

                switch (userContext.ContextType) {
                    case Dashboard.ContextTypes.BrandContext:
                        filter.BrandId = userContext.BrandId;
                        break;
                    case Dashboard.ContextTypes.CallCenterContext:
                        filter.CallCenterId = userContext.CallCenterId;
                        break;
                    case Dashboard.ContextTypes.StoreContext:
                        filter.StoreId = userContext.StoreId;
                        break;
                }

                return self.queryServer(endpoint, "POST", filter);
            },

            /**
             * Gets all order channels.
             * @returns {Promise}
             */
            async getOrderChannels() {
                const endpoint = "mapi/order/channels";
                return self.queryServer(endpoint, "GET");
            },

            /**
             * Gets an order by its id.
             * @param orderGuid - The GUID for the order.
             * @returns {Object} - The order object.
             */
            getOrder: function (orderGuid) {
                let endpoint = "mapi/order";
                endpoint = endpoint.appendQuerystringParameter("orderGuid", orderGuid);
                return self.queryServer(endpoint, "GET");
            },
        };

        self.Campaigns = {
            getPublishedCampaigns: function (storeId, brandId) {
                let endpoint = "/mapi/campaigns/published";

                if (storeId) {
                    endpoint = endpoint.appendQuerystringParameter("storeId", storeId);
                } else if (brandId) {
                    endpoint = endpoint.appendQuerystringParameter("brandId", brandId);
                }

                return self.queryServer(endpoint, "GET");
            },

            getFavouriteCampaigns: function (storeId, brandId) {
                let endpoint = "/mapi/campaigns/favourites";

                if (storeId) {
                    endpoint = endpoint.appendQuerystringParameter("storeId", storeId);
                } else if (brandId) {
                    endpoint = endpoint.appendQuerystringParameter("brandId", brandId);
                }

                return self.queryServer(endpoint, "GET");
            },

            getCampaignStatusCount: function (storeId, brandId) {
                let endpoint = "/mapi/campaigns/count";
                if (storeId) {
                    endpoint = endpoint.appendQuerystringParameter("storeId", storeId);
                } else if (brandId) {
                    endpoint = endpoint.appendQuerystringParameter("brandId", brandId);
                }
                return self.queryServer(endpoint, "GET");
            },

            isCampaignTemplateCurrentlyActive: function (campaignTemplateId) {
                if (!isKey(campaignTemplateId)) {
                    const result = $q.defer();
                    result.resolve({ data: false });
                    return result.promise;
                }
                return self.queryServer(
                    `/mapi/campaigns/is-template-active/${campaignTemplateId}`,
                    "GET"
                );
            },

            revertCampaignTemplateToDraft: function (campaignTemplateId) {
                if (typeof campaignTemplateId !== "number") {
                    throw new Error(`Invalid campaignTemplateId: '${campaignTemplateId}'`);
                }
                return self.queryServer(
                    `/mapi/campaigns/revert-template-to-draft/${campaignTemplateId}`,
                    "POST"
                );
            },

            getActiveCampaigns: function (storeId, brandId, campaignTemplateId) {
                const definedCount = [ storeId, brandId, campaignTemplateId ].reduce(
                    (acc, cur) => acc += cur ? 1 : 0,
                    0);
                if (definedCount > 1) {
                    throw new Error(`you may (currently) only query by one of storeId, brandId, campaignTemplateId`);
                }
                let endpoint = "/mapi/campaigns/active";

                if (storeId) {
                    endpoint = endpoint.appendQuerystringParameter("storeId", storeId);
                } else if (brandId) {
                    endpoint = endpoint.appendQuerystringParameter("brandId", brandId);
                } else if (campaignTemplateId) {
                    endpoint = endpoint.appendQuerystringParameter(
                        "campaignTemplateId",
                        campaignTemplateId
                    );
                }

                return self.queryServer(endpoint, "GET");
            },

            getScheduledCampaigns: function (storeId, brandId) {
                let endpoint = "/mapi/campaigns/scheduled";

                if (storeId) {
                    endpoint = endpoint.appendQuerystringParameter("storeId", storeId);
                } else if (brandId) {
                    endpoint = endpoint.appendQuerystringParameter("brandId", brandId);
                }

                return self.queryServer(endpoint, "GET");
            },

            getCancelledCampaigns: function (storeId, brandId, page, count, queryText) {
                let endpoint = "/mapi/campaigns/cancelled";

                if (storeId) {
                    endpoint = endpoint.appendQuerystringParameter("storeId", storeId);
                } else if (brandId) {
                    endpoint = endpoint.appendQuerystringParameter("brandId", brandId);
                }

                const filter = {
                    pageNumber: page,
                    pageSize: count,
                    query: queryText,
                    orderByLatest: true
                };

                endpoint = endpoint.appendQuerystringParameters(filter);

                return self.queryServer(endpoint, "GET");
            },

            getCompletedCampaigns: function (
                storeId,
                brandId,
                page,
                count,
                queryText
            ) {
                let endpoint = "/mapi/campaigns/complete";

                if (storeId) {
                    endpoint = endpoint.appendQuerystringParameter("storeId", storeId);
                } else if (brandId) {
                    endpoint = endpoint.appendQuerystringParameter("brandId", brandId);
                }

                const filter = {
                    pageNumber: page,
                    pageSize: count,
                    query: queryText,
                    orderByLatest: true
                };

                endpoint = endpoint.appendQuerystringParameters(filter);

                return self.queryServer(endpoint, "GET");
            },

            /**
             * Returns a campaign template by id
             * @param campaignTemplateId {number}
             * @param storeId {number}
             * @param brandId {number}
             */
            getCampaignTemplate: function (campaignTemplateId, storeId, brandId) {
                let endpoint = "/mapi/campaigns/template";

                endpoint = endpoint.appendQuerystringParameter("campaignTemplateId", campaignTemplateId);

                if (storeId) {
                    endpoint = endpoint.appendQuerystringParameter("storeId", storeId);
                } else if (brandId) {
                    endpoint = endpoint.appendQuerystringParameter("brandId", brandId);
                }

                return self.queryServer(endpoint, "GET");
            },

            getCampaignTemplateValidEndDates: function (campaignTemplateId, storeId, brandId, startDate) {
                let endpoint = "/mapi/campaigns/validenddates";

                endpoint = endpoint.appendQuerystringParameter("campaignTemplateId", campaignTemplateId);

                if (storeId) {
                    endpoint = endpoint.appendQuerystringParameter("storeId", storeId);
                } else if (brandId) {
                    endpoint = endpoint.appendQuerystringParameter("brandId", brandId);
                }

                endpoint = endpoint.appendQuerystringParameter("startDate", startDate.toUTCString());

                return self.queryServer(endpoint, "GET");
            },

            publishCampaign: function (campaign) {
                const endpoint = "/mapi/campaigns/activate";

                return self.queryServer(endpoint, "POST", campaign);
            },

            cancelCampaign: function (campaignId) {
                let endpoint = "mapi/campaigns/cancel";
                endpoint = endpoint.appendQuerystringParameter("id", campaignId);
                return self.queryServer(endpoint, "POST");
            },

            /**
             * Gets the number of customers for a campaign and store
             * @param campaignType {string}
             * @param storeId {number}
             */
            getNumberOfCustomersForCampaign: function (campaignType, storeId) {
                let endpoint = "/mapi/campaign";

                endpoint = endpoint.appendQuerystringParameter("campaignType", campaignType);
                endpoint = endpoint.appendQuerystringParameter("storeId", storeId);

                return self.queryServer(endpoint, "GET");
            },

            createCampaign: function (campaign) {
                const endpoint = "/mapi/campaign";

                return self.queryServer(endpoint, "POST", campaign);
            },

            getCustomerEstimatesFromCampaignTargetSourceId: function (
                campaignTargetSourceId,
                campaignTemplateGuid,
                brandId,
                storeId
            ) {
                let endpoint = "mapi/campaigns/template/customerestimates";

                endpoint = endpoint.appendQuerystringParameter("campaignTargetSourceId", campaignTargetSourceId);
                endpoint = endpoint.appendQuerystringParameter("campaignTemplateGuid", campaignTemplateGuid);
                endpoint = endpoint.appendQuerystringParameter("brandId", brandId);

                if (storeId) {
                    endpoint = endpoint.appendQuerystringParameter("storeId", storeId);
                }

                return self.queryServer(endpoint, "GET");
            }
        };

        /**
         * All API calls related to campaign designer
         */
        self.CampaignDesigner = {
            getCampaignTemplatesByState: function (templateState,
                                                   brandId) {
                const endpoint = "mapi/campaign/templates/bystate/{0}/{1}".format(templateState, brandId);

                return self.queryServer(endpoint, "GET");
            },

            getCampaignTemplate: function (campaignTemplateGuid) {
                const endpoint = "/mapi/campaign/template/{0}".format(campaignTemplateGuid);

                return self.queryServer(endpoint, "GET");
            },

            /**
             * Creates the campaign template record and saves the basic detalis.
             * @param campaignTemplate {CampaignTemplate}
             * @returns {promise}
             */
            saveBasicSetup: function (campaignTemplate) {
                const endpoint = "/mapi/campaign/template/save";

                return self.queryServer(endpoint, "PUT", campaignTemplate);
            },

            updateCampaignTemplateCommunication: function (
                campaignTemplateGuid,
                communication,
                limitToSingleCommunication) {
                const endpoint = "/mapi/campaign/template/{0}/communication"
                    .format(campaignTemplateGuid);

                return self.queryServer(endpoint,
                    "PUT", {
                        communication: communication,
                        limitToSingleCommunication: limitToSingleCommunication
                    });
            },

            updateCampaignTemplateStores: function (campaignTemplateGuid, stores, brandId) {
                const endpoint = "/mapi/campaign/template/{0}/stores"
                    .format(campaignTemplateGuid);

                const request = {
                    Stores: stores,
                    BrandId: brandId
                };
                return self.queryServer(endpoint, "PUT", request);
            },

            saveCampaignTemplateVoucher: function (campaignTemplateGuid, campaignTemplateVoucher) {
                const endpoint = "/mapi/campaign/template/{0}/voucher"
                    .format(campaignTemplateGuid);

                return self.queryServer(endpoint, "PUT", campaignTemplateVoucher);
            },

            removeCampaignTemplateVoucher: function (campaignTemplateGuid, campaignTemplateVoucherId) {
                const endpoint = "/mapi/campaign/template/{0}/voucher/{1}"
                    .format(campaignTemplateGuid, campaignTemplateVoucherId);

                return self.queryServer(endpoint, "DELETE", null);
            },

            saveCampaignTemplateMenuscript: function (campaignTemplateGuid, campaignTemplateMenuScript) {
                const endpoint = "/mapi/campaign/template/{0}/menuscript"
                    .format(campaignTemplateGuid);

                return self.queryServer(endpoint, "PUT", campaignTemplateMenuScript);
            },

            publishCampaignTemplate: function (campaignTemplateGuid) {
                const endpoint = "/mapi/campaign/template/{0}/publish"
                    .format(campaignTemplateGuid);

                return self.queryServer(endpoint, "PUT");
            },

            removeCampaignTemplateMenuscript: function (campaignTemplateGuid, campaignTemplateMenuscriptId) {
                const endpoint = "/mapi/campaign/template/{0}/menuscript/{1}"
                    .format(campaignTemplateGuid, campaignTemplateMenuscriptId);

                return self.queryServer(endpoint, "DELETE", null);
            },

            getCustomerWorkflowTypes: function () {
                const endpoint = "/mapi/campaign/template/workflowtypes";

                return self.queryServer(endpoint, "GET");
            },

            getCampaignObjectives: function () {
                const endpoint = "/mapi/campaign/template/objectives";

                return self.queryServer(endpoint, "GET");
            },
            archiveCampaignTemplate: function (campaignTemplateGuid) {
                if (!campaignTemplateGuid) {
                    throw new Error("campaignTemplateGuid is not provided");
                }
                const endpoint = `/mapi/campaign/template/${campaignTemplateGuid}/archive`;
                return self.queryServer(endpoint, "PUT");
            },
            unArchiveCampaignTemplate: function (campaignTemplateGuid) {
                if (!campaignTemplateGuid) {
                    throw new Error("campaignTemplateGuid is not provided");
                }
                const endpoint = `/mapi/campaign/template/${campaignTemplateGuid}/unarchive`;
                return self.queryServer(endpoint, "PUT");
            },
            removeCampaignTemplateCommunication: function (campaignTemplateGuid, campaignTemplateCommunicationId) {
                const endpoint = "/mapi/campaign/template/{0}/communication/{1}"
                    .format(campaignTemplateGuid,
                        campaignTemplateCommunicationId);

                return self.queryServer(
                    endpoint,
                    "DELETE",
                    null);
            },

            getTargetSourcesForFilter: function (take,
                                                 skip,
                                                 filter) {
                let endpoint = "/mapi/campaign/targetsources";

                const request = {
                    Skip: skip,
                    Take: take,
                    CampaignNameFilter: filter.Name,
                    BrandId: filter.BrandId,
                    Type: filter.Type,
                    Status: filter.Status
                };

                endpoint = endpoint.appendQuerystringParameters(request);
                return self.queryServer(endpoint, "GET");
            },

            getTargetSourceById: function (targetSourceId) {
                let endpoint = `/mapi/campaign/targetsources/${targetSourceId}`;

                return self.queryServer(endpoint, "GET");
            },

            saveTargetSource: function (targetSource) {
                const endpoint = "/mapi/campaign/targetsources/save";
                return self.queryServer(endpoint, "PUT", targetSource);
            },

            testTargetSource: function (
                source,
                brandId,
                testStoreId) {
                let endpoint = source.appendQuerystringParameter("mode", "count");
                endpoint = endpoint.appendQuerystringParameter("BrandId", brandId);
                if (testStoreId) {
                    endpoint = endpoint.appendQuerystringParameter("StoreId", testStoreId);
                }
                return self.queryUrl(endpoint);
            },
            uploadFeaturedImage: function (templateGuid, files) {
                const endpoint = `/mapi/campaign/template/${templateGuid}/featured-image`
                const formData = new FormData();
                const file = files[0];
                formData.append('files', file, file.filename);

                return self.queryServer(endpoint, "POST", formData, {
                    headers: {
                        'Content-Type': undefined
                    }
                });
            },
            clearFeaturedImage: function (campaignTemplateGuid) {
                const endpoint = `/mapi/campaign/template/${campaignTemplateGuid}/featured-image`
                return self.queryServer(endpoint, "DELETE")
            }
        };

        self.Templates = {
            getInboxTemplatesForBrand: function (brandId) {
                let endpoint = "/mapi/brands/templates";
                endpoint = endpoint.appendQuerystringParameter("brandId", brandId);
                return self.queryServer(endpoint, "GET");
            }
        };

        self.OutOfStock = {
            getModel: function (storeId) {
                let endpoint = "/mapi/outofstock";
                endpoint = endpoint.appendQuerystringParameter("storeId", storeId);
                return self.queryServer(endpoint, "GET");
            },
            associateStoreToMenuScript: function (
                storeId,
                menuScriptGuid,
                expiryDays) {
                let endpoint = "/mapi/outofstock/addscript";
                endpoint = endpoint.appendQuerystringParameter("storeId", storeId);
                endpoint = endpoint.appendQuerystringParameter("menuScriptGuid", menuScriptGuid);
                endpoint = endpoint.appendQuerystringParameter("expiryDays", expiryDays);
                return self.queryServer(endpoint, "PUT");
            },
            cancelMenuScript: function (
                storeId,
                menuScriptGuid
            ) {
                let endpoint = "/mapi/outofstock/cancelscript";
                endpoint = endpoint.appendQuerystringParameter("storeId", storeId);
                endpoint = endpoint.appendQuerystringParameter("menuScriptGuid", menuScriptGuid);
                return self.queryServer(endpoint, "PUT");
            },
            getPreviousRequests: function (
                storeId) {
                let endpoint = "/mapi/outofstock/requests";
                endpoint = endpoint.appendQuerystringParameter("storeId", storeId);
                return self.queryServer(endpoint, "GET");
            },
            submitRequest: function (
                storeId,
                itemName,
                itemDescription,
                expiryDays) {
                let endpoint = "/mapi/outofstock/request";
                endpoint = endpoint.appendQuerystringParameter("storeId", storeId);
                endpoint = endpoint.appendQuerystringParameter("itemName", itemName);
                endpoint = endpoint.appendQuerystringParameter("itemDescription", itemDescription);
                endpoint = endpoint.appendQuerystringParameter("expiryDays", expiryDays);
                return self.queryServer(endpoint, "PUT");
            }
        };

        self.DeliveryZones = {
            getDeliveryZonesForStore: function (storeId) {
                let endpoint = "/mapi/store/deliveryzones";
                endpoint = endpoint.appendQuerystringParameter("storeId", storeId);
                return self.queryServer(endpoint, "GET", null, {
                    showLoader: true
                });
            },
            setDeliveryZones: function (
                storeId,
                deliveryZones) {
                let endpoint = "/mapi/store/deliveryzones";
                return self.queryServer(endpoint,
                    "PUT", {
                        storeId,
                        deliveryZones
                    });
            }
        };
    }

    function isKey(value) {
        if (!value) {
            return false;
        }
        if (typeof value !== "number") {
            return true;
        }
        return value > 0;
    }

    angular.module("dashboard")
        .service("APIService",
            [
                "$http",
                "$timeout",
                "$q",
                "DashboardConfig",
                "$state",
                APIService
            ]
        );
})();
